// imperial/groceryProcessorImperial.js - Grocery processing for imperial measurement system
import { 
  normalizeToImperialBase, 
  calculateTotalCostImperial,
  // categorizeProductsImperial // Removed import
} from './groceryUtilsImperial';
import { normalizeIngredientName, getIngredientCategory, findProductMetadata } from '../groceryUtils';
import { processUnmatchedIngredientsWithAI } from '../aiGroceryFallback';
import { normalizeCategory, PRODUCT_METADATA, FOOD_DENSITIES, UNIT_CONVERSIONS } from '../groceryConstants';

/**
 * Default average weights for unit products by category (in ounces)
 * Used when avgWeight is not available in product data or PRODUCT_METADATA
 */
const DEFAULT_AVG_WEIGHTS_IMPERIAL = {
  'Produce': 5.3,       // ~150g in oz
  'Meat & Seafood': 7.1, // ~200g in oz
  'Dairy, Alternatives & Eggs': 2.1, // ~60g in oz
  'Bakery': 1.8,        // ~50g in oz
  'Frozen': 3.5,        // ~100g in oz
  'Pantry': 3.5,        // ~100g in oz
  'Condiments': 1.8,    // ~50g in oz
  'Other': 3.5,         // ~100g in oz
};

/**
 * Get avgWeight for a product, with fallback to PRODUCT_METADATA and category defaults
 * @param {Object} size - Product size object
 * @param {String} productName - Product name for metadata lookup
 * @param {String} category - Product category for default fallback
 * @returns {Number|null} Average weight in ounces, or null if truly unavailable
 */
const getAvgWeightWithFallbackImperial = (size, productName, category) => {
  // 1. Try size.avgWeight first
  const sizeAvgWeight = parseFloat(size?.avgWeight);
  if (!isNaN(sizeAvgWeight) && sizeAvgWeight > 0) {
    return sizeAvgWeight;
  }
  
  // 2. Try PRODUCT_METADATA
  if (productName) {
    const metadata = findProductMetadata(productName);
    if (metadata?.avgWeight?.imperial) {
      return metadata.avgWeight.imperial;
    }
  }
  
  // 3. Fallback to category defaults
  const normalizedCategory = normalizeCategory(category || 'Other');
  return DEFAULT_AVG_WEIGHTS_IMPERIAL[normalizedCategory] || DEFAULT_AVG_WEIGHTS_IMPERIAL['Other'];
};

/**
 * Process a meal plan into a grocery list using imperial measurements
 * @param {Array} mealPlan - The meal plan with recipes
 * @param {Array} countryProducts - Product database for the country
 * @param {Object} ctx - Context object with country, currency, etc.
 * @param {Number} numberOfPeople - Number of people
 * @param {Function} onProgress - Progress callback
 * @param {Object} options - Processing options (e.g., skipAIFallback)
 * @returns {Object} Processed grocery list
 */
export const processGroceryListImperial = async (mealPlan, countryProducts, ctx, numberOfPeople, onProgress = null, options = {}) => {
  try {
    if (onProgress) {
      onProgress({
        step: 'imperial-processing',
        progress: 30,
        message: 'Consolidating ingredients (imperial)...',
      });
    }

    // 1. Consolidate ingredients
    const consolidatedIngredients = consolidateIngredientsImperial(mealPlan, numberOfPeople);

    if (onProgress) {
      onProgress({
        step: 'matching',
        progress: 50,
        message: 'Matching ingredients to products...',
      });
    }
    
    // 2. Match ingredients to products
    const matchedProducts = matchIngredientsToProductsImperial(consolidatedIngredients, countryProducts);

    // 3. AI fallback for unmatched ingredients (before quantity calculations)
    const unmatchedItems = matchedProducts.filter(item => item.unmatched);
    let allProducts = [...matchedProducts];
    
    if (unmatchedItems.length > 0 && !options.skipAIFallback) {
      if (onProgress) {
        onProgress({
          step: 'ai_fallback',
          progress: 60,
          message: `Processing ${unmatchedItems.length} unmatched ingredients with AI...`,
        });
      }
      
      const aiResults = await processUnmatchedIngredientsWithAI(unmatchedItems, ctx.country);
      
      // Replace unmatched items with AI results
      allProducts = matchedProducts.filter(item => !item.unmatched);
      allProducts.push(...aiResults);
    } else if (options.skipAIFallback) {
      // Skip AI processing and only use matched products
      allProducts = matchedProducts.filter(item => !item.unmatched);
      if (onProgress && unmatchedItems.length > 0) {
        onProgress({
          step: 'skipping-ai',
          progress: 60,
          message: `Skipping AI processing for ${unmatchedItems.length} unmatched ingredients...`,
        });
      }
    }

    if (onProgress) {
      onProgress({
        step: 'calculating',
        progress: 70,
        message: 'Calculating quantities and prices (imperial)...',
      });
    }
    
    // 4. Calculate purchase quantities (unified for both database and AI products)
    const calculatedProducts = calculatePurchaseQuantitiesImperial(
      allProducts, 
      countryProducts, 
      ctx.currency,
      ctx.userSegment || 'standard' // Default to standard segment if not specified
    );

    // 5. Finalize grocery list
    if (onProgress) {
      onProgress({
        step: 'finalizing',
        progress: 90,
        message: 'Finalizing grocery list...',
      });
    }
    
    // Removed: const categorizedGroceryList = categorizeProductsImperial(calculatedProducts);
    const totalCost = calculateTotalCostImperial(calculatedProducts, ctx.currency);
    const leftovers = calculateLeftoversImperial(calculatedProducts);
    const maxStorageTime = determineMaxStorageTimeImperial(calculatedProducts);

    // Group products by normalized category directly from calculatedProducts
    const grouped = {};
    calculatedProducts.forEach(item => { // Changed from categorizedGroceryList
      const cat = normalizeCategory(item.category || 'Other');
      if (!grouped[cat]) grouped[cat] = { category: cat, items: [] };

      // Enrich with storage data from PRODUCT_METADATA (reliable source for 770+ products)
      let enrichedItem = item;
      if (!item.storage) {
        const meta = findProductMetadata(item.name) || findProductMetadata(item.originalName) || findProductMetadata(item.productName);
        if (meta?.storage) {
          enrichedItem = { ...item, storage: meta.storage, shelfLifeDays: meta.shelfLifeDays || item.shelfLifeDays || 0 };
        }
      }

      grouped[cat].items.push(enrichedItem);
    });

    if (onProgress) {
      onProgress({
        step: 'complete',
        progress: 100,
        message: 'Grocery list processing complete!',
      });
    }

    return {
      // Removed products from intermediate result
      totalCost,
      leftovers,
      maxStorageTime,
      products: Object.values(grouped) // Return the correctly grouped products
    };
  } catch (error) {
    console.error('Error in processGroceryListImperial:', error);
    if (onProgress) {
      onProgress({ step: 'error', message: error.message });
    }
    return { error: error.message };
  }
};

/**
 * Consolidate ingredients from recipes using imperial system
 * @param {Array} mealPlan - Array of recipes
 * @param {Number} numberOfPeople - Number of people to cook for
 * @returns {Array} Consolidated ingredients
 */
export const consolidateIngredientsImperial = (mealPlan, numberOfPeople) => {
  const ingredientsMap = {};

  mealPlan.forEach(recipe => {
    if (!Array.isArray(recipe.ingredients)) return;

    recipe.ingredients.forEach(ingredient => {
      const originalName = ingredient.product || '';
      const normalizedName = normalizeIngredientName(originalName);

      // Parse quantity using imperial system
      let qtyValue = 1;
      let qtyUnit = 'piece'; 

      if (ingredient.qty) {
        const qtyStr = String(ingredient.qty).toLowerCase().trim();
        
        // Try fraction parsing first: handles "1/2 tsp", "3/4 cup", "1 1/2 tbsp"
        const mixedMatch = qtyStr.match(/^(\d+)\s+(\d+)\/(\d+)\s*(.*)$/);
        const fractionMatch = !mixedMatch && qtyStr.match(/^(\d+)\/(\d+)\s*(.*)$/);
        
        if (mixedMatch) {
          const whole = parseFloat(mixedMatch[1]);
          const num = parseFloat(mixedMatch[2]);
          const den = parseFloat(mixedMatch[3]);
          qtyValue = den > 0 ? whole + num / den : whole;
          qtyUnit = (mixedMatch[4] || "").trim() || 'piece';
        } else if (fractionMatch) {
          const num = parseFloat(fractionMatch[1]);
          const den = parseFloat(fractionMatch[2]);
          qtyValue = den > 0 ? num / den : 1;
          qtyUnit = (fractionMatch[3] || "").trim() || 'piece';
        } else {
          const match = qtyStr.match(/^([\d.]+)\s*(.*)$/);
          if (match) {
            qtyValue = parseFloat(match[1]) || 1;
            qtyUnit = match[2].trim() || 'piece';
          }
        }
      }

      if (qtyUnit === "to taste") {
        const productMeta = PRODUCT_METADATA[normalizedName];
        if (productMeta && productMeta.avgWeight) {
          qtyValue = productMeta.avgWeight.imperial || 1; // Default to 1 if avgWeight is missing
          qtyUnit = productMeta.marketWeight.imperial || "oz"; // Default to ounces
        }
      } else if (UNIT_CONVERSIONS.volume.conversions[qtyUnit]) {
        // Keep volume units as volume for liquids - don't convert to weight
        qtyValue *= UNIT_CONVERSIONS.volume.conversions[qtyUnit];
        qtyUnit = UNIT_CONVERSIONS.volume.baseUnit; // Convert to base volume unit (fl oz)
      } else if (UNIT_CONVERSIONS.weight.conversions[qtyUnit]) {
        // Convert weight to base unit
        qtyValue *= UNIT_CONVERSIONS.weight.conversions[qtyUnit];
        qtyUnit = UNIT_CONVERSIONS.weight.baseUnit;
      }

      // NOTE: We do NOT multiply by numberOfPeople here!
      // The AI prompt already calculates quantities for the target number of people.
      // Multiplying again would cause double-counting (e.g., 6 eggs × 6 people = 36 eggs instead of 6)

      // Normalize to imperial base units
      const normalizedQty = normalizeToImperialBase(qtyValue, qtyUnit, normalizedName);
      const key = `${normalizedName}_${normalizedQty.measurementType}_${normalizedQty.unit}`;
      
      if (!ingredientsMap[key]) {
        ingredientsMap[key] = {
          name: normalizedName,
          originalName,
          allOriginals: [originalName],
          totalQty: normalizedQty.value,
          unit: normalizedQty.unit,
          measurementType: normalizedQty.measurementType,
          originalUnit: normalizedQty.originalUnit,
          usedIn: [recipe.title],
        };
      } else {
        ingredientsMap[key].totalQty += normalizedQty.value;
        if (!ingredientsMap[key].usedIn.includes(recipe.title)) {
          ingredientsMap[key].usedIn.push(recipe.title);
        }
        if (!ingredientsMap[key].allOriginals.includes(originalName)) {
          ingredientsMap[key].allOriginals.push(originalName);
        }
      }
    });
  });

  return Object.values(ingredientsMap);
};

/**
 * Match ingredients to products in the database (imperial)
 * @param {Array} ingredients - Consolidated ingredients list
 * @param {Array} products - Country-specific product database
 * @returns {Array} Matched products
 */
export const matchIngredientsToProductsImperial = (ingredients, products) => {
  return ingredients.map(item => {
    // Find all potential matches by name or synonyms
    const potentialMatches = products.filter(p => {
      // Check direct name match
      if (normalizeIngredientName(p.name) === item.name) {
        return true;
      }
      
      // Check product synonyms (if they exist in the product)
      if (p.synonyms && p.synonyms.some(s => normalizeIngredientName(s) === item.name)) {
        return true;
      }
      
      // Check metadata synonyms for this product ID
      const metadata = findProductMetadata(p.id || p.name);
      if (metadata && metadata.synonyms) {
        return metadata.synonyms.some(syn => normalizeIngredientName(syn) === item.name);
      }
      
      return false;
    });

    if (potentialMatches.length === 0) {
      return { 
        ...item, 
        unmatched: true, 
        matchConfidence: 0.0,
        category: getIngredientCategory(item.name)
      };
    }

    // If there are multiple matches, prefer appropriate product types
    let bestMatch = potentialMatches[0];
    
    if (potentialMatches.length > 1) {
      // For count measurements (piece), prefer unit products over weight products
      if (item.measurementType === 'count') {
        const unitProducts = potentialMatches.filter(p => 
          p.sizes && p.sizes.some(size => size.type === 'unit')
        );
        if (unitProducts.length > 0) {
          bestMatch = unitProducts[0];
        }
      }
      // For weight measurements, prefer weight products over unit products
      else if (item.measurementType === 'weight') {
        const weightProducts = potentialMatches.filter(p => 
          p.sizes && p.sizes.some(size => size.type === 'weight')
        );
        if (weightProducts.length > 0) {
          bestMatch = weightProducts[0];
        }
      }
      // For volume measurements, prefer unit products (bottles, cartons) over weight products
      else if (item.measurementType === 'volume') {
        const unitProducts = potentialMatches.filter(p => 
          p.sizes && p.sizes.some(size => size.type === 'unit' && 
            (size.weightUnit === 'fl oz' || size.weightUnit === 'qt' || size.weightUnit === 'gal' || size.weightUnit === 'pint'))
        );
        if (unitProducts.length > 0) {
          bestMatch = unitProducts[0];
        }
      }
    }

    // Always get canonical category from PRODUCT_METADATA
    const meta = findProductMetadata(bestMatch.id || bestMatch.name);
    const canonicalCategory = meta ? meta.category : getIngredientCategory(item.name);

    return {
      ...item,
      productId: bestMatch.id,
      productName: bestMatch.name,
      category: canonicalCategory,
      perishable: !!bestMatch.perishable,
      shelfLifeDays: bestMatch.shelf_life_days || bestMatch.shelfLifeDays || 0,
      // Smart recalculation tracking
      source: 'database', // Track that this came from database
      originalIngredientNames: [item.name], // Track which ingredient names this covers
      unmatched: false,
      matchConfidence: 1.0,
      product: bestMatch // Keep reference to full product for later
    };
  });
};

/**
 * Calculate purchase details based on product type and requirements (imperial)
 * @param {Object} size - Selected product size
 * @param {number} needInBase - Required quantity in base units
 * @param {string} measurementType - Type of measurement
 * @param {string} productName - Product name for metadata lookup
 * @param {number} originalWeightNeed - Original weight requirement (for unit products converted from weight)
 * @returns {Object} Purchase details
 */
const calculatePurchaseDetailsImperial = (size, needInBase, measurementType, productName = null, originalWeightNeed = null) => {
  const details = {
    quantityToPurchase: 0,
    itemsQuantity: 1,
    unitSize: '',
    leftovers: 0,
    leftoverPercentage: '0%'
  };

  switch (size.type) {
    case 'unit':
      // Fix: Handle volume-based unit products (like milk cartons)
      if (measurementType === 'volume' && size.weight && size.weightUnit) {
        let unitVolume = size.weight;
        
        // Convert to fl oz if needed
        if (size.weightUnit === 'qt' || size.weightUnit === 'quart') {
          unitVolume = size.weight * 32;
        } else if (size.weightUnit === 'gal' || size.weightUnit === 'gallon') {
          unitVolume = size.weight * 128;
        } else if (size.weightUnit === 'pint') {
          unitVolume = size.weight * 16;
        }
        
        const volumeNeeded = needInBase; // needInBase is in fl oz
        const unitsNeeded = Math.ceil(volumeNeeded / unitVolume);
        details.itemsQuantity = unitsNeeded;
        details.quantityToPurchase = unitsNeeded * unitVolume;
        details.unitSize = `${unitsNeeded} × ${size.description || 'unit'} (${unitVolume} fl oz each)`;
        
        const totalVolume = unitsNeeded * unitVolume;
        details.leftovers = Math.max(0, totalVolume - volumeNeeded);
        details.leftoverPercentage = totalVolume > 0 
          ? `${Math.round((details.leftovers / totalVolume) * 100)}%` 
          : '0%';
      } 
      // Fix: Handle weight-based requirements for unit products with avgWeight
      else if (measurementType === 'weight') {
        // Convert weight requirement to unit count using avgWeight
        const unitAvgWeightNum = parseFloat(size.avgWeight);
        const hasUnitAvgWeight = !isNaN(unitAvgWeightNum) && unitAvgWeightNum > 0;
        
        if (hasUnitAvgWeight) {
          const unitsNeeded = Math.ceil(needInBase / unitAvgWeightNum);
          const totalWeight = unitsNeeded * unitAvgWeightNum;
        
        details.itemsQuantity = unitsNeeded;
        details.quantityToPurchase = unitsNeeded; // Keep as unit count for unit products
        details.unitSize = `${unitsNeeded} × ${size.description || 'unit'}`;
        
          // Fix: Calculate leftovers based on actual weight
          details.leftovers = Math.max(0, totalWeight - needInBase);
          details.leftoverPercentage = totalWeight > 0 
            ? `${Math.round((details.leftovers / totalWeight) * 100)}%` 
            : '0%';
        } else {
          // Standard unit calculation without avgWeight
          const unitsNeeded = Math.ceil(needInBase);
          details.itemsQuantity = unitsNeeded;
          details.quantityToPurchase = unitsNeeded;
          details.unitSize = `${unitsNeeded} × ${size.description || 'unit'}`;
        }
      }
      else {
        // Standard unit calculation
        const unitsNeeded = Math.ceil(needInBase);
        details.itemsQuantity = unitsNeeded;
        details.quantityToPurchase = unitsNeeded;
        details.unitSize = `${unitsNeeded} × ${size.description || 'unit'}`;
        
        // Fix: Calculate leftovers if avgWeight available
        if (size.avgWeight) {
          const totalWeight = unitsNeeded * size.avgWeight;
          // Use originalWeightNeed if available (for unit products converted from weight requirements)
          const weightNeeded = originalWeightNeed || (needInBase * size.avgWeight);
          details.leftovers = Math.max(0, totalWeight - weightNeeded);
          details.leftoverPercentage = totalWeight > 0 
            ? `${Math.round((details.leftovers / totalWeight) * 100)}%` 
            : '0%';
        }
      }
      break;

    case 'weight':
      // For weight products being purchased by piece count
      const wgtAvgWeightNum = parseFloat(size.avgWeight);
      const hasWgtAvgWeight = !isNaN(wgtAvgWeightNum) && wgtAvgWeightNum > 0;
      
      if (measurementType === 'count' && hasWgtAvgWeight) {
        const weightNeeded = needInBase * wgtAvgWeightNum;
        details.itemsQuantity = needInBase; // Number of pieces
        details.quantityToPurchase = weightNeeded;
        details.unitSize = `${needInBase} × ${size.description || 'item'} (~${weightNeeded}oz)`;
        details.leftovers = 0; // No leftovers for exact weight purchases
      } else if ((hasWgtAvgWeight || (productName && findProductMetadata(productName)?.avgWeight)) && productName) {
        // Use smart weight sizing for weight products with avgWeight or metadata avgWeight
        const metadata = findProductMetadata(productName);
        const smartSizing = calculateSmartWeightSizingImperial(size, needInBase, metadata, 'imperial');
        return smartSizing;
      } else {
        // Standard weight purchase
        details.itemsQuantity = 1; // Always 1 for weight products
        details.quantityToPurchase = needInBase;
        details.unitSize = `${needInBase}${size.weightUnit || 'oz'}`;
        
        // Add approximate count if avgWeight available
        if (hasWgtAvgWeight) {
          const approxCount = Math.round(needInBase / size.avgWeight);
          details.unitSize += ` (approximately ${approxCount} items)`;
        }
        details.leftovers = 0; // No leftovers for exact weight purchases
      }
      break;

    case 'package':
      // Fix: Handle both weight-based and count-based packages properly
      if (size.weight && size.weight > 0) {
        // Weight-based package (e.g., spinach 16oz package)
        const packagesNeeded = Math.ceil(needInBase / size.weight);
        details.itemsQuantity = packagesNeeded;
        details.quantityToPurchase = packagesNeeded * size.weight;
        details.unitSize = `${packagesNeeded} × ${size.description || 'package'} (${size.weight}${size.weightUnit || 'oz'} each)`;
        
        const totalPackageWeight = packagesNeeded * size.weight;
        details.leftovers = Math.max(0, totalPackageWeight - needInBase);
        details.leftoverPercentage = totalPackageWeight > 0 
          ? `${Math.round((details.leftovers / totalPackageWeight) * 100)}%` 
          : '0%';
      } else if (size.itemsQty && size.itemsQty > 0) {
        // Count-based package (e.g., eggs 12-pack)
        const packagesNeeded = Math.ceil(needInBase / size.itemsQty);
        details.itemsQuantity = packagesNeeded;
        details.quantityToPurchase = packagesNeeded * size.itemsQty;
        details.unitSize = `${packagesNeeded} × ${size.description || 'package'} (${size.itemsQty} items each)`;
        
        const totalItems = packagesNeeded * size.itemsQty;
        details.leftovers = Math.max(0, totalItems - needInBase);
        details.leftoverPercentage = totalItems > 0 
          ? `${Math.round((details.leftovers / totalItems) * 100)}%` 
          : '0%';
      } else {
        // Fallback for packages without clear weight or count info
        details.itemsQuantity = 1;
        details.quantityToPurchase = needInBase;
        details.unitSize = `1 × ${size.description || 'package'}`;
        details.leftovers = 0;
      }
      break;
  }

  return details;
};

/**
 * Calculate purchase quantities for matched products (imperial) using three-parameter evaluation
 * @param {Array} matchedProducts - Products matched to ingredients
 * @param {Array} countryProducts - Country product database
 * @param {String} currencySymbol - Currency symbol
 * @param {String} userSegment - User's preferred price segment (default: 'standard')
 * @returns {Array} Products with purchase quantities and prices
 */
export const calculatePurchaseQuantitiesImperial = (matchedProducts, countryProducts, currencySymbol, userSegment = 'standard') => {
  return matchedProducts.map(item => {
    if (item.unmatched) return item;

    const product = item.product;
    if (!product || !product.sizes) return item;

    // How much we need in base units (oz, lb, or pieces)
    let needInBase = item.totalQty;

    // Apply three-parameter evaluation system
    const bestOption = evaluateProductOptionsImperial(product.sizes, needInBase, userSegment, item.measurementType);
    
    if (!bestOption) {
      return { ...item, unmatched: true, error: 'No suitable product size found' };
    }

    // FIX: For unit products with weight requirements, convert properly
    // Keep track of original weight for leftover calculation
    let originalWeightNeed = null;
    if (
      bestOption.type === "unit" &&
      item.measurementType === "weight" &&
      bestOption.avgWeight &&
      bestOption.avgWeight > 0
    ) {
      // Store original weight requirement for accurate leftover calculation
      originalWeightNeed = needInBase;
      
      // Convert required weight to count
      const unitsNeeded = Math.ceil(needInBase / bestOption.avgWeight);
      console.log(
        `🔧 [Imperial] Converting ${needInBase}oz to ${unitsNeeded} units for ${item.name} (avgWeight: ${bestOption.avgWeight}oz)`
      );
      needInBase = unitsNeeded;

      // Ensure measurement type is updated for proper calculation
      item.measurementType = "count";
    }

    // Calculate purchase details based on product type
    const purchaseDetails = calculatePurchaseDetailsImperial(bestOption, needInBase, item.measurementType, item.name, originalWeightNeed);

    // Calculate actual price based on product type and quantity needed
    let actualPrice = 0;
    
    switch (bestOption.type) {
      case 'package':
        // For packages, charge for the minimal number of complete packages needed
        if (bestOption.weight && bestOption.weight > 0) {
          // Weight-based package: calculate minimum packages needed
          const packagesNeeded = Math.ceil(needInBase / bestOption.weight);
          actualPrice = bestOption.price * packagesNeeded;
        } else if (bestOption.itemsQty && bestOption.itemsQty > 0) {
          // Count-based package: calculate minimum packages needed
          const packagesNeeded = Math.ceil(needInBase / bestOption.itemsQty);
          actualPrice = bestOption.price * packagesNeeded;
        } else {
          // Fallback: full package price
          actualPrice = bestOption.price;
        }
        break;
        
      case 'unit':
        // Unit products: price per unit × number of units needed
        // Special handling for liquid units with volume measurements
        if (bestOption.weightUnit && (bestOption.weightUnit === 'fl oz' || bestOption.weightUnit === 'qt' || bestOption.weightUnit === 'gal' || bestOption.weightUnit === 'pint')) {
          // Liquid unit product (milk, juice, etc.)
          if (item.measurementType === 'volume') {
            // Need specific amount of liquid, calculate how many units to buy
            let unitVolume = bestOption.weight;
            
            // Convert to fl oz if needed
            if (bestOption.weightUnit === 'qt' || bestOption.weightUnit === 'quart') {
              unitVolume = bestOption.weight * 32; // Convert quarts to fl oz
            } else if (bestOption.weightUnit === 'gal' || bestOption.weightUnit === 'gallon') {
              unitVolume = bestOption.weight * 128; // Convert gallons to fl oz
            } else if (bestOption.weightUnit === 'pint') {
              unitVolume = bestOption.weight * 16; // Convert pints to fl oz
            }
            
            const volumeNeeded = needInBase; // needInBase is in fl oz
            const unitsNeeded = Math.ceil(volumeNeeded / unitVolume);
            actualPrice = bestOption.price * unitsNeeded;
          } else {
            // Count-based requirement for liquid units
            actualPrice = bestOption.price * purchaseDetails.itemsQuantity;
          }
        } 
        // Fix: Handle weight-based requirements for unit products properly
        else if (item.measurementType === 'weight' && bestOption.avgWeight && bestOption.avgWeight > 0) {
          // For weight-based requirements on unit products, charge per unit needed
          actualPrice = bestOption.price * purchaseDetails.itemsQuantity;
        }
        else if (bestOption.itemsQty && bestOption.itemsQty > 0) {
          const pricePerUnit = bestOption.price / bestOption.itemsQty;
          actualPrice = pricePerUnit * purchaseDetails.itemsQuantity;
        } else {
          actualPrice = bestOption.price * purchaseDetails.itemsQuantity;
        }
        break;
        
      case 'weight':
        // Weight products: proportional pricing based on weight
        if (bestOption.weight && bestOption.weight > 0) {
          // Fix: Handle unit conversion for weight pricing
          let priceBaseWeight = bestOption.weight;
          if (bestOption.weightUnit === 'lb') {
            priceBaseWeight = bestOption.weight * 16; // Convert to ounces
          }
          
          const pricePerOz = bestOption.price / priceBaseWeight;
          actualPrice = pricePerOz * purchaseDetails.quantityToPurchase;
        } 
        // Fix: Special case for count-based items with avgWeight
        else if (bestOption.avgWeight && bestOption.avgWeight > 0 && item.measurementType === 'count') {
          // Calculate price based on actual weight of pieces being purchased
          const totalWeightOz = purchaseDetails.itemsQuantity * bestOption.avgWeight;
          const pricePerOz = bestOption.price / bestOption.weight;
          actualPrice = pricePerOz * totalWeightOz;
        }
        // If the ingredient is sold by weight but we're buying individual pieces
        else if (bestOption.avgWeight && bestOption.avgWeight > 0) {
          // Calculate price based on actual weight of pieces being purchased
          const actualWeight = purchaseDetails.itemsQuantity * bestOption.avgWeight;
          const pricePerBaseUnit = bestOption.price / bestOption.weight;
          actualPrice = pricePerBaseUnit * actualWeight;
        }
        else {
          // Fallback: full package price
          actualPrice = bestOption.price;
        }
        break;
        
      default:
        actualPrice = bestOption.price;
    }

    // Apply price adjustment from smart sizing if available
    // REMOVED: This was causing unrealistic price reductions  
    // if (purchaseDetails.priceAdjustment) {
    //   actualPrice *= purchaseDetails.priceAdjustment;
    // }

    return {
      ...item,
      ...purchaseDetails,
      totalPrice: `${currencySymbol}${actualPrice.toFixed(2)}`,
      selectedSize: bestOption
    };
  });
};

/**
 * Three-parameter evaluation system for product selection (imperial)
 * @param {Array} sizes - Available product sizes
 * @param {number} needInBase - Required quantity in base units
 * @param {string} userSegment - User's preferred segment
 * @param {string} measurementType - Type of measurement (weight, volume, count)
 * @returns {Object|null} Best product option
 */
const evaluateProductOptionsImperial = (sizes, needInBase, userSegment, measurementType) => {
  if (!sizes || sizes.length === 0) return null;

  // Define segment hierarchy (lowest to highest)
  const segmentHierarchy = ['survival', 'budget', 'standard', 'gourmet', 'luxury'];
  const userSegmentIndex = segmentHierarchy.indexOf(userSegment);
  
  // Step 1: Filter suitable options (can satisfy requirement)
  const suitableOptions = sizes.filter(size => {
    return isSizeSuitableImperial(size, needInBase, measurementType);
  });

  if (suitableOptions.length === 0) return null;

  // Step 2: Apply segment priority - try user segment first, then scope down
  for (let i = userSegmentIndex; i >= 0; i--) {
    const targetSegment = segmentHierarchy[i];
    const segmentOptions = suitableOptions.filter(size => 
      (size.segment || 'standard') === targetSegment
    );

    if (segmentOptions.length > 0) {
      // Step 3: Price optimization within segment
      const cheapestPrice = Math.min(...segmentOptions.map(size => size.price));
      const cheapestOptions = segmentOptions.filter(size => size.price === cheapestPrice);

      // Step 4: Rating tie-breaking (if multiple options within 10% price difference)
      if (cheapestOptions.length === 1) {
        return cheapestOptions[0];
      }

      // Check for price differences within 10%
      const priceRangeOptions = segmentOptions.filter(size => {
        const priceDiff = (size.price - cheapestPrice) / cheapestPrice;
        return priceDiff <= 0.10;
      });

      // Return highest rated option within price range
      return priceRangeOptions.reduce((best, current) => {
        const bestRating = best.rating || 3.0;
        const currentRating = current.rating || 3.0;
        return currentRating > bestRating ? current : best;
      });
    }
  }

  // Fallback: cheapest option if no segment match
  return suitableOptions.reduce((cheapest, current) => 
    current.price < cheapest.price ? current : cheapest
  );
};

/**
 * Check if a product size can satisfy the requirement (imperial)
 * @param {Object} size - Product size option
 * @param {number} needInBase - Required quantity in base units
 * @param {string} measurementType - Type of measurement
 * @returns {boolean} Whether size is suitable
 */
const isSizeSuitableImperial = (size, needInBase, measurementType) => {
  switch (size.type) {
    case 'unit':
      // For unit products, handle weight vs count measurement types properly
      const availableQty = size.itemsQty || 1;
      
      // Parse avgWeight to handle both string and number types
      const impAvgWeightNum = parseFloat(size.avgWeight);
      const hasImpAvgWeight = !isNaN(impAvgWeightNum) && impAvgWeightNum > 0;
      
      if (measurementType === 'weight' && hasImpAvgWeight) {
        // For unit products with weight requirements, avgWeight must be reasonable (>= 0.35oz ~= 10g)
        // If avgWeight < 0.35oz, it's likely invalid data or a package weight, not per-unit weight
        if (impAvgWeightNum < 0.35) {
          console.warn(`Unit product has unreasonably small avgWeight (${impAvgWeightNum}oz) for weight measurement - rejecting`);
          return false;
        }
        
        // Convert weight requirement to unit count using avgWeight
        const unitsNeeded = Math.ceil(needInBase / impAvgWeightNum);
        
        // Check if available units can satisfy the requirement
        if (unitsNeeded <= availableQty) {
          return true;
        }
        
        // Allow up to 2x flexibility for weight-to-unit conversion
        if (unitsNeeded <= availableQty * 2) {
          return true;
        }
        
        return false;
      } else if (measurementType === 'volume' && size.weight && size.weightUnit) {
        // Fix: For volume-based unit products (like milk cartons, juice boxes)
        // Check if the unit's volume can satisfy the volume requirement
        let unitVolume = size.weight; // Volume in fl oz (assuming weight field holds volume for liquids)
        
        // Convert to fl oz if needed
        if (size.weightUnit === 'qt' || size.weightUnit === 'quart') {
          unitVolume = size.weight * 32; // Convert quarts to fl oz
        } else if (size.weightUnit === 'gal' || size.weightUnit === 'gallon') {
          unitVolume = size.weight * 128; // Convert gallons to fl oz
        } else if (size.weightUnit === 'pint') {
          unitVolume = size.weight * 16; // Convert pints to fl oz
        }
        
        // Check if one unit can satisfy the requirement
        if (needInBase <= unitVolume) {
          return true;
        }
        
        // Smart limits for volume products (imperial)
        const unitsNeeded = Math.ceil(needInBase / unitVolume);
        
        // Tier 1: Perfect fit
        if (unitsNeeded <= 1) {
          return true;
        }
        
        // Tier 2: Reasonable flexibility (up to 2x)
        if (unitsNeeded <= 2) {
          return true;
        }
        
        // Tier 3: Practical limit for volume products (up to 4x)
        // Volume products often come in standard sizes, allow more flexibility
        if (unitsNeeded <= 4) {
          return true;
        }
        
        return false;
      } else if (size.weight && size.weightUnit && (size.weightUnit === 'fl oz' || size.weightUnit === 'qt' || size.weightUnit === 'gal' || size.weightUnit === 'pint')) {
        // Handle liquid unit products that might have count measurementType but are actually volume-based
        // This covers cases like milk cartons where the recipe says "1 carton" but it's really about volume
        let unitVolume = size.weight;
        
        // Convert to fl oz if needed
        if (size.weightUnit === 'qt' || size.weightUnit === 'quart') {
          unitVolume = size.weight * 32;
        } else if (size.weightUnit === 'gal' || size.weightUnit === 'gallon') {
          unitVolume = size.weight * 128;
        } else if (size.weightUnit === 'pint') {
          unitVolume = size.weight * 16;
        }
        
        // For count-based requirements of liquid products, assume each "count" represents the full container
        // So if they need 1 carton and we have 1 carton available, it's suitable
        if (measurementType === 'count') {
          const requiredQty = needInBase;
          
          // Smart limits for count-based liquid products
          if (requiredQty <= availableQty) {
            return true;
          }
          
          // Allow reasonable flexibility for buying multiple liquid units (up to 4x)
          if (requiredQty <= availableQty * 4) {
            return true;
          }
          
          return false;
        }
        
        return false;
      } else {
        // Smart limits for count measurements (imperial system)
        const requiredQty = needInBase;
        const availableQty = size.itemsQty || 1;
        
        // Tier 1: Perfect fit (ideal scenario)
        if (requiredQty <= availableQty) {
          return true;
        }
        
        // Tier 2: Reasonable flexibility (up to 2x - handles fractional needs)
        // Example: Need 1.3 cucumbers, buy 2 → reasonable
        if (requiredQty <= availableQty * 2) {
          return true;
        }
        
        // Tier 3: Practical limit (up to 4x for imperial - slightly more permissive)
        // Imperial system allows a bit more flexibility for cultural shopping patterns
        if (requiredQty <= availableQty * 4) {
          return true;
        }
        
        // Tier 4: For multi-pack products, check if we need small multiples
        // Example: Need 8 individual items, buy 4 × 2-packs = 8 items
        if (requiredQty <= availableQty * 4) {
          return true;
        }
        
        // Tier 4: Reject unrealistic scenarios
        // Example: Need 50 individual steaks, only 1-packs → suggest bulk/wholesale
        return false;
      }
    
    case 'weight':
      // For weight products, we can buy exact amount (minimum check could be added)
      // Special handling for piece-based requirements on weight products
      const wgtImpAvgWeightNum = parseFloat(size.avgWeight);
      const hasWgtImpAvgWeight = !isNaN(wgtImpAvgWeightNum) && wgtImpAvgWeightNum > 0;
      
      if (measurementType === 'count' && hasWgtImpAvgWeight) {
        // Ensure this weight product can supply the required pieces based on avgWeight
        return hasWgtImpAvgWeight; // As long as avgWeight exists, we can calculate
      }
      return true;
    
    case 'package':
      // Fix: For packages, always allow - we can buy multiple packages if one isn't enough
      // This handles cases like needing 32oz but only having 12oz packages (buy 3 packages)
      if (size.weight && size.weight > 0) {
        // Weight-based package: always suitable (can buy multiple)
        return true;
      } else if (size.itemsQty && size.itemsQty > 0) {
        // Count-based package: always suitable (can buy multiple)
        return true;
      } else {
        // Fallback: allow package if no clear quantity info
        return true;
      }
    
    default:
      return false;
  }
};

/**
 * Calculate smart weight product sizing with avgWeight-based variants (imperial)
 * @param {Object} size - Weight product size
 * @param {number} needInBase - Required quantity in ounces
 * @param {Object} metadata - Product metadata with avgWeight
 * @param {string} system - Measurement system ('metric' or 'imperial')
 * @returns {Object} Optimal sizing details
 */
const calculateSmartWeightSizingImperial = (size, needInBase, metadata, system = 'imperial') => {
  if (!metadata?.avgWeight?.[system] && !size.avgWeight) {
    // Fallback to standard weight calculation without smart sizing
    return {
      quantityToPurchase: needInBase,
      itemsQuantity: 1,
      unitSize: `${needInBase}oz`,
      leftovers: 0,
      leftoverPercentage: '0%',
      unitInfo: null,
      priceAdjustment: 1.0
    };
  }

  const avgWeight = size.avgWeight || metadata.avgWeight[system];
  
  // Calculate small/medium/large variants (±25% of avgWeight)
  const variants = {
    small: avgWeight * 0.75,
    medium: avgWeight,
    large: avgWeight * 1.25
  };

  // Find best combination with minimal leftover
  const combinations = [];
  
  // Try different combinations of small/medium/large units
  for (let large = 0; large <= Math.ceil(needInBase / variants.large) + 1; large++) {
    for (let medium = 0; medium <= Math.ceil((needInBase - large * variants.large) / variants.medium) + 1; medium++) {
      const remaining = needInBase - (large * variants.large) - (medium * variants.medium);
      const small = remaining > 0 ? Math.ceil(remaining / variants.small) : 0;
      
      const totalWeight = (large * variants.large) + (medium * variants.medium) + (small * variants.small);
      
      if (totalWeight >= needInBase) {
        const leftover = totalWeight - needInBase;
        const leftoverPercentage = (leftover / totalWeight) * 100;
        
        combinations.push({
          large,
          medium,
          small,
          totalWeight,
          leftover,
          leftoverPercentage,
          totalUnits: large + medium + small
        });
      }
    }
  }

  // Find combination with minimal leftover percentage
  const bestCombination = combinations.reduce((best, current) => {
    if (current.leftoverPercentage < best.leftoverPercentage) {
      return current;
    }
    if (current.leftoverPercentage === best.leftoverPercentage && current.totalUnits < best.totalUnits) {
      return current;
    }
    return best;
  }, combinations[0]);

  if (!bestCombination) {
    // Fallback if no suitable combination found
    return {
      quantityToPurchase: needInBase,
      itemsQuantity: 1,
      unitSize: `${needInBase}oz`,
      leftovers: 0,
      leftoverPercentage: '0%',
      unitInfo: null,
      priceAdjustment: 1.0
    };
  }

  // Create unit information string
  const unitParts = [];
  const unitBreakdown = [];
  if (bestCombination.large > 0) {
    unitParts.push(`${bestCombination.large} large (~${Math.round(variants.large * 10) / 10}oz each)`);
    unitBreakdown.push({
      size: 'large',
      count: bestCombination.large,
      weightPerUnit: Math.round(variants.large * 10) / 10,
      weightUnit: 'oz',
    });
  }
  if (bestCombination.medium > 0) {
    unitParts.push(`${bestCombination.medium} medium (~${Math.round(variants.medium * 10) / 10}oz each)`);
    unitBreakdown.push({
      size: 'medium',
      count: bestCombination.medium,
      weightPerUnit: Math.round(variants.medium * 10) / 10,
      weightUnit: 'oz',
    });
  }
  if (bestCombination.small > 0) {
    unitParts.push(`${bestCombination.small} small (~${Math.round(variants.small * 10) / 10}oz each)`);
    unitBreakdown.push({
      size: 'small',
      count: bestCombination.small,
      weightPerUnit: Math.round(variants.small * 10) / 10,
      weightUnit: 'oz',
    });
  }

  const unitInfo = unitParts.join(' + ');
  const totalUnits = bestCombination.totalUnits;

  // Calculate price adjustment if leftover exceeds 5%
  let priceAdjustment = 1.0;
  if (bestCombination.leftoverPercentage > 5) {
    // Reduce effective price proportionally to account for leftover
    priceAdjustment = needInBase / bestCombination.totalWeight;
  }

  return {
    quantityToPurchase: Math.round(bestCombination.totalWeight * 10) / 10, // Round to 1 decimal for ounces
    itemsQuantity: totalUnits,
    unitSize: `${Math.round(bestCombination.totalWeight * 10) / 10}oz (~${totalUnits} units)`,
    leftovers: Math.round(bestCombination.leftover * 10) / 10,
    leftoverPercentage: `${Math.round(bestCombination.leftoverPercentage)}%`,
    unitInfo,
    unitBreakdown,
    priceAdjustment,
    smartSizing: true
  };
};

/**
 * Calculate leftovers from grocery list (imperial)
 * @param {Array} list - Complete grocery list
 * @returns {Array} Leftover items
 */
export const calculateLeftoversImperial = (list) => {
  return list
    .filter((item) => item.leftovers && item.leftovers > 0)
    .map((item) => {
      // Determine the unit based on the product type and how leftovers were calculated
      let leftoverUnit = 'units';
      const selectedSize = item.selectedSize;
      const productType = selectedSize?.type;
      
      // The leftover unit depends on how the product is sold, not how it was requested
      if (productType === 'package') {
        // Package leftovers: check if it's weight-based or count-based
        if (selectedSize?.weight && selectedSize?.weight > 0) {
          // Weight-based package - leftover is in oz
          leftoverUnit = 'oz';
        } else if (selectedSize?.itemsQty && selectedSize?.itemsQty > 0) {
          // Count-based package (e.g., 6-pack eggs) - leftover is in units
          leftoverUnit = ' units';
        } else {
          leftoverUnit = ' units';
        }
      } else if (productType === 'weight') {
        // Weight products - leftover is in oz
        leftoverUnit = 'oz';
      } else if (productType === 'unit') {
        // Unit products with avgWeight - leftover is in oz (calculated from weight)
        if (selectedSize?.avgWeight && selectedSize?.avgWeight > 0) {
          leftoverUnit = 'oz';
        } else {
          leftoverUnit = ' units';
        }
      } else {
        // Fallback based on measurementType
        const measurementType = item.measurementType;
        if (measurementType === 'volume') {
          leftoverUnit = 'fl oz';
        } else if (measurementType === 'weight') {
          leftoverUnit = 'oz';
        } else {
          leftoverUnit = ' units';
        }
      }
      
      // Format the leftover amount with unit
      const leftoverValue = Math.round(item.leftovers * 10) / 10;
      const formattedLeftoverAmount = `${leftoverValue}${leftoverUnit}`;
      
      return {
        productName: item.productName || item.name,
        productId: item.productId,
        selectedSize: item.selectedSize,
        leftoverAmount: formattedLeftoverAmount,
        leftoverValue: leftoverValue, // Raw value for calculations
        leftoverUnit: leftoverUnit.trim(),
        leftoverPercentage: item.leftoverPercentage,
        category: item.category,
        perishable: item.perishable,
        shelfLifeDays: item.shelfLifeDays,
      };
    });
};

/**
 * Determine maximum storage time for the grocery list
 * @param {Array} list - Complete grocery list
 * @returns {String} Maximum storage time description
 */
export const determineMaxStorageTimeImperial = (list) => {
  let maxDays = 0;
  list.forEach(item => {
    if (item.perishable && item.shelfLifeDays) {
      maxDays = Math.max(maxDays, item.shelfLifeDays);
    }
  });
  return maxDays > 0 ? `${maxDays} days` : '';
};